import { Trans, useLingui } from "@lingui/react/macro";
import { MessageCircle, Minus, RotateCcw, Send, X } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ApiConnector } from "../../api/api-connector";
import { Button } from "../../components/ui-kit/button";
import { Spinner } from "../../components/ui-kit/spinner";
import styles from "./chat-markdown.module.css";
import { createChatModel } from "./chat-model";

export const ChatBubble = observer(function ChatBubble({
  api,
}: {
  api: ApiConnector;
}) {
  const { t } = useLingui();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const chat = useMemo(() => createChatModel(api), [api]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const loadingWord = useLoadingWord(chat.loading);

  useEffect(() => () => chat.dispose(), [chat]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chat.messages.length, chat.activeTools.length, scrollToBottom]);

  useEffect(() => {
    if (open && !minimized) {
      textareaRef.current?.focus();
    }
  }, [open, minimized]);

  const handleNewConversation = () => {
    chat.newConversation();
    textareaRef.current?.focus();
  };

  const floatingBottom = "calc(1.5rem + var(--chat-bubble-offset, 0px))";

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ bottom: floatingBottom }}
        className="fixed right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-[bottom,colors] cursor-pointer"
        aria-label={t`Open Justin AI`}
      >
        <MessageCircle size={24} />
      </button>
    );
  }

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        style={{ bottom: floatingBottom }}
        className="fixed right-6 z-50 flex h-14 items-center gap-2 rounded-full bg-primary text-primary-foreground shadow-lg px-5 hover:bg-primary/90 transition-[bottom,colors] cursor-pointer"
        aria-label={t`Expand Justin AI`}
      >
        <MessageCircle size={20} />
        <span className="text-sm font-medium">Justin AI</span>
        {chat.loading && (
          <span className="text-xs italic animate-pulse">{loadingWord}</span>
        )}
      </button>
    );
  }

  return (
    <div
      style={{ bottom: floatingBottom }}
      className="fixed top-20 right-6 z-50 flex flex-col w-105 rounded-xl border bg-popover text-popover-foreground shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-muted/30 rounded-t-xl">
        <span className="text-sm font-semibold">Justin AI</span>
        <div className="flex items-center gap-1">
          {chat.messages.length > 0 && (
            <button
              onClick={handleNewConversation}
              className="p-1.5 rounded hover:bg-muted transition-colors cursor-pointer"
              title={t`New conversation`}
            >
              <RotateCcw size={14} />
            </button>
          )}
          <button
            onClick={() => setMinimized(true)}
            className="p-1.5 rounded hover:bg-muted transition-colors cursor-pointer"
            title={t`Minimize`}
          >
            <Minus size={14} />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="p-1.5 rounded hover:bg-muted transition-colors cursor-pointer"
            title={t`Close`}
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="grow overflow-y-auto px-4 py-3 space-y-3">
        {chat.messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            <p>
              <Trans>Ask about patients, alerts, or monitoring data.</Trans>
            </p>
          </div>
        )}

        {chat.messages.map((msg, i) => (
          <div key={i}>
            {msg.role === "user" ? (
              <div className="flex justify-end">
                <div className="max-w-[85%] rounded-lg px-3 py-2 text-sm bg-primary text-primary-foreground whitespace-pre-wrap">
                  {msg.content}
                </div>
              </div>
            ) : (
              <div className="flex justify-start">
                <div className="max-w-[85%] space-y-1">
                  {/* Tool call badges */}
                  {msg.toolCalls && msg.toolCalls.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-1">
                      {msg.toolCalls.map((tool, j) => (
                        <span
                          key={j}
                          className="inline-flex items-center gap-1 rounded-full bg-info-soft border border-info-border px-2 py-0.5 text-[11px] text-info-foreground"
                        >
                          {chat.activeTools.includes(tool) && (
                            <Spinner size={10} />
                          )}
                          {tool}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* Message content with markdown */}
                  {msg.content ? (
                    <div
                      className={`rounded-lg px-3 py-2 text-sm bg-muted ${styles.markdown}`}
                    >
                      <Markdown remarkPlugins={[remarkGfm]}>
                        {msg.content}
                      </Markdown>
                    </div>
                  ) : (
                    chat.loading &&
                    i === chat.messages.length - 1 && (
                      <div className="rounded-lg px-3 py-2 bg-muted text-sm text-muted-foreground italic animate-pulse">
                        {loadingWord}
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t">
        <div className="flex gap-2 items-end">
          <textarea
            ref={textareaRef}
            value={chat.input}
            onChange={(e) => (chat.input = e.target.value)}
            onKeyDown={(e) => chat.handleKeyDown(e)}
            placeholder={t`Ask a question...`}
            className="grow min-h-[40px] max-h-[100px] resize-none rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            disabled={chat.loading}
          />
          <Button
            variant="primary"
            size="sm"
            icon={Send}
            label={t`Send`}
            onClick={() => void chat.sendMessage()}
            pending={chat.loading}
            disabled={!chat.input.trim()}
          />
        </div>
      </div>
    </div>
  );
});

// Loading word animation

const LOADING_WORDS = [
  "Thinking...",
  "Timberlejking...",
  "Defibrilating...",
  "Arrhythmiing...",
  "Systolizing...",
  "PPG-waving...",
  "Lub-dubbing...",
  "Left-ventrickling...",
  "Troponining...",
  "Photoplethysmographing...",
  "Repolarizing...",
  "Diastolicking...",
  "Tachythinking...",
  "Seerlinqing...",
];

function useLoadingWord(active: boolean) {
  const [index, setIndex] = useState(() =>
    Math.floor(Math.random() * LOADING_WORDS.length),
  );
  useEffect(() => {
    if (!active) return;
    setIndex(Math.floor(Math.random() * LOADING_WORDS.length));
    const interval = setInterval(() => {
      setIndex(Math.floor(Math.random() * LOADING_WORDS.length));
    }, 4000);
    return () => clearInterval(interval);
  }, [active]);
  return LOADING_WORDS[index];
}
